import { z } from 'zod/v4-mini';

import {
  ApiDataSourceSpec,
  apiDataSource,
} from '@/io/datasource/ApiDatasource';
import {
  SessionStorageDataSourceSpec,
  sessionStorageDataSource,
} from '@/io/datasource/SessionStorageDatasource.ts';
import {
  ChatVisibilityType,
  CreateMessage,
  Message,
  messageSchema,
} from '@/models/ChatModel';
import { cz } from '@/utils/zodUtils.ts';

export class ChatRepository {
  constructor(
    private readonly datasource: ApiDataSourceSpec = apiDataSource,
    private readonly sessionDatasource: SessionStorageDataSourceSpec = sessionStorageDataSource,
  ) {}

  getMessages = (
    subjectId: string,
    visibility: ChatVisibilityType,
  ): Promise<Message[]> => {
    return this.datasource.get(
      `/practitioner/messages/${subjectId}/`,
      cz.forcedArray(messageSchema),
      {
        params: {
          visibility: visibility,
        },
      },
    );
  };

  createMessage = (
    patientId: string,
    data: CreateMessage,
  ): Promise<Message> => {
    return this.datasource.post(
      `/practitioner/messages/${patientId}/`,
      messageSchema,
      this._prepareMessageData(data),
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      },
    );
  };

  _prepareMessageData = (data: CreateMessage) => {
    const { contentAttachment, ...restData } = data;
    const formData = new FormData();

    if (contentAttachment) {
      for (const i of contentAttachment) {
        if (i.file) {
          formData.append('attachment', i.file);
        }
      }
    }

    for (const itemIndex in restData) {
      const itemData = restData[itemIndex as keyof typeof restData];
      if (itemData) {
        formData.append(itemIndex, itemData);
      }
    }
    formData.append('status', 'completed');

    return formData;
  };

  deleteMessage = (
    patientId: string,
    messageId: string,
  ): Promise<undefined> => {
    return this.datasource.put(
      `/practitioner/messages/${patientId}/${messageId}/`,
      cz.ignore(),
      {
        status: 'stopped',
      },
    );
  };

  draftMessage = (userId: string, patientId: string, message: string) => {
    return this.sessionDatasource.store(
      `${userId}-${patientId}`,
      'message',
      message,
    );
  };

  clearDraftMessage = (userId: string, patientId: string) => {
    return this.sessionDatasource.remove(`${userId}-${patientId}`, 'message');
  };

  getDraftMessage = (practitionerId: string, patientId: string) => {
    return this.sessionDatasource.retrieve(
      `${practitionerId}-${patientId}`,
      'message',
      z.string(),
    );
  };
}
